import {
  NavStore,
  CompendiumStore,
  PilotStore,
  NpcStore,
  UserStore,
  NarrativeStore,
  EncounterStore,
  CampaignStore,
} from '@/stores'
import { collectionDataQuery } from '@/user/api'

import { Initialize } from './Storage'
import { AchievementManager } from '@/user/achievements/AchievementManager'
import { UnauthorizedError } from '@/io/apis/account'
import logger from '@/user/logger'
import { migrateV2LocalStorage } from './FullImporter'

export default async function (skipSync = false): Promise<void> {
  UserStore().IsLoading = true
  await Initialize()

  navigator.storage
    .estimate()
    .then(res => logger.info(`Storage estimate: ${res.usage} / ${res.quota}`))

  logger.info('loading user')
  await UserStore().loadUser()

  // Migrate v2 localStorage data before loading compendium content
  const migrationResult = await migrateV2LocalStorage()
  if (migrationResult) {
    logger.info('v2 localStorage migration complete', migrationResult)
    // TODO: expose migrationResult to UI for summary display and .compcon download offer
  }

  logger.info('refreshing extra content')
  await CompendiumStore().refreshExtraContent()
  logger.info('extra content refreshed')

  logger.info('loading user data...')
  await Promise.all([
    PilotStore()
      .LoadPilots()
      .then(() => logger.info('pilots loaded')),
    NpcStore()
      .LoadNpcs()
      .then(() => logger.info('npcs loaded')),
    NarrativeStore()
      .LoadCollectionItems()
      .then(() => logger.info('narrative collection items loaded')),
    EncounterStore()
      .LoadEncounters()
      .then(() => logger.info('encounters loaded')),
    CampaignStore()
      .LoadCampaigns()
      .then(() => logger.info('campaigns loaded')),
  ])
  logger.info('data loaded')

  logger.info('removing old data')
  await UserStore().removeOldItems()

  await NavStore().CreateIndex()

  if (UserStore().Cognito.userId) {
    UserStore().IsSyncing = true
    try {
      await UserStore().AutoSync(undefined, true, skipSync)
      await UserStore().getRemoteCollectionMetadata(true)
      if (UserStore().UserMetadata.CollectionSubscriptionSettings.updateOn === 'startup') {
        logger.info('Auto updating remote collections')
        UserStore().updateAllRemoteCollections()
        logger.info('Auto update complete!')
      }
      logger.info('Auto sync complete!')
    } catch (error: any) {
      if (error instanceof UnauthorizedError) {
        logger.warn('Authentication expired during startup sync — continuing offline')
      } else {
        logger.error(`Failed to sync: ${error}`, {}, error)
      }
      UserStore().IsSyncing = false
    }

    // Skip remaining cloud operations if the user was signed out
    if (UserStore().IsLoggedIn) {
      logger.info('checking v2 cloud migration status')
      await UserStore().checkV2CloudMigration()

      logger.info('checking auto backups')
      try {
        await UserStore().PruneBackups()
        await UserStore().AutoBackup()
      } catch (error: any) {
        logger.error(`Failed to backup: ${error}`, {}, error)
      }

      try {
        const patreonStatus = await UserStore().refreshPatreonData()
        if (patreonStatus === 'success') {
          logger.info('Patreon data refreshed')
        } else {
          logger.error('Failed to refresh Patreon data')
        }
      } catch (error: any) {
        logger.error(`Failed to refresh Patreon data: ${error}`, {}, error)
      }

      const subscribedLcps = UserStore().User.LcpSubscriptions
      if (subscribedLcps.length > 0) {
        const remoteLcps = await collectionDataQuery('lcp')
        for (const lcp of remoteLcps) {
          if (!subscribedLcps.includes(lcp.sortkey)) continue

          const installedPack = CompendiumStore().ContentPacks.find(
            p => p.Manifest.name === lcp.name || p.Manifest.name === lcp.title
          )
          if (!installedPack || installedPack.Manifest.version < lcp.version) {
            try {
              await UserStore().downloadLcp(lcp)
              UserStore().addCloudNotification(`Updated ${lcp.name} to ${lcp.version}.`)
            } catch (error: any) {
              logger.error('Failed to download lcp:', lcp.name, error)
              UserStore().addCloudNotification(`Failed to download ${lcp.name}!`, 'error')
              logger.error(`Error downloading LCP: ${error}`, {}, error)
            }
          }
        }
      }

      CompendiumStore().loadContentCollections()
      UserStore().setSyncTimer()
      UserStore().IsSyncing = false
    }
  }

  try {
    AchievementManager.Instantiate()
  } catch (error: any) {
    logger.error(`Failed to load achievements: ${error}`, {}, error)
  }

  UserStore().IsLoading = false
}
